/**
 * MapSearchBar.tsx — Address search → geocode → flyTo + ScorePanel (MAP-02)
 *
 * Flow:
 *  1. User types an address and submits.
 *  2. POST /api/geocode { query } → [{ lat, lng, displayName, confidence? }] — takes the top result.
 *  3. map.flyTo({ center: [lng, lat], zoom: 12 }) (MAP-02).
 *  4. POST /api/events/search { address, lat, lng } — engagement logging (AUTH-04/DEMO-02).
 *  5. GET /api/tracts/at?lat&lng → the census tract whose geometry CONTAINS the point
 *     (point-in-polygon, T-153/BEAD-0060 — replaced a bbox-first-feature lookup that
 *     returned an arbitrary neighbour and mislabelled the panel with the wrong GEOID).
 *  6. Calls onSearchResult(tract, location, confidence) so MapShell can show the ScorePanel
 *     with the appropriate ConfidenceBadge.
 *
 * Plan 02.1-06: confidence field added to GeocodeResult (from Plan 04 API change).
 * onSearchResult extended with 3rd arg: confidence: 'exact' | 'approximate'.
 * Defaults to 'approximate' if API does not return a confidence value.
 *
 * Auth note: authedFetch handles DEV_AUTH_BYPASS locally; no token hardcoded here.
 */
import { useEffect, useRef, useState } from 'react'
import { useMap } from './MapContext'
import { POI_MIN_ZOOM } from './POILayerMgr'
import { Spinner } from '../ui/spinner'
import { authedFetch } from '../../lib/api'
import { featurePropsToTract } from '../../lib/tractModel'
import { useRecentSearches } from '../../hooks/useRecentSearches'
import type { CensusTract, TractFeatureProps } from '../../lib/tractModel'

export interface MapSearchBarProps {
  /**
   * BEAD-0038: `tract` is nullable. An out-of-coverage search still fires this callback
   * (with tract:null) so MapShell can run the shared score→404→"No coverage" path instead
   * of silently panning the map with no feedback.
   */
  onSearchResult: (
    tract: CensusTract | null,
    location: { lat: number; lng: number },
    confidence: 'exact' | 'approximate',
    address: string,
  ) => void
  /** Plan 02-04: hides the click-to-drop hint once a pin has been placed. */
  pinPlaced?: boolean
  /**
   * T-429: fires whenever the search goes in or out of use — focused, or still holding a
   * query after focus left. The caller uses it to move overlays off the suggestion dropdown
   * (on a phone the EmptyState card sits right on top of it). Reported on FOCUS rather than
   * on the first keystroke, so the very first suggestion is already unobstructed.
   */
  onSearchActiveChange?: (active: boolean) => void
}

interface GeocodeResult {
  lat: number
  lng: number
  displayName: string
  /** Plan 02.1-04: server-derived from Nominatim class field. */
  confidence?: 'exact' | 'approximate'
}

/** T-134: one search-as-you-type suggestion from GET /api/geocode/autocomplete. */
interface AutocompleteSuggestion {
  text: string
  placeId: string
}

// ── T-134 autocomplete tuning (named so they are trivially tunable) ──────────────
// Trailing debounce: wait this long after the last keystroke before querying, so fast
// typing collapses to a single call. Kept in sync with the server-side cost guard.
const AUTOCOMPLETE_DEBOUNCE_MS = 300
// Minimum trimmed length before we issue any autocomplete request (a product requirement:
// no GeoPlaces call on a couple of keystrokes). The server enforces the same threshold.
const AUTOCOMPLETE_MIN_CHARS = 3

export function MapSearchBar({
  onSearchResult,
  pinPlaced,
  onSearchActiveChange,
}: MapSearchBarProps) {
  const map = useMap()
  const [query, setQuery] = useState('')
  const [focused, setFocused] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showRecent, setShowRecent] = useState(false)
  const {
    items: recentSearches,
    add: addRecentSearch,
    clear: clearRecentSearches,
  } = useRecentSearches()

  // T-134: search-as-you-type suggestions + keyboard highlight index (-1 = none).
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([])
  const [activeIndex, setActiveIndexState] = useState(-1)
  // Mirror the highlight in a ref so a rapid ArrowDown→Enter burst (multiple key events in
  // one React batch, before a re-render) reads the latest index rather than a stale closure.
  const activeIndexRef = useRef(-1)
  function setActiveIndex(next: number) {
    activeIndexRef.current = next
    setActiveIndexState(next)
  }

  // BEAD-0037: abort any in-flight geocode/tract search when the search bar unmounts
  // (sign-out tears down the whole map subtree). Without this, a fetch that resolves
  // after unmount would drive map.flyTo() against a removed MapLibre instance and throw.
  const abortRef = useRef<AbortController | null>(null)
  // T-134: a separate controller for autocomplete so a superseded suggestion request is
  // cancelled without disturbing an in-flight full search (they have independent lifecycles).
  const acAbortRef = useRef<AbortController | null>(null)
  // Set when focus selected the existing query, and consumed by the very next mouseup (T-236).
  // A click focuses the field and THEN places the caret, which would collapse the selection we just
  // made — so the first mouseup after such a focus is suppressed, and only that one. A second click
  // behaves completely normally, which is what keeps click-to-position available for editing.
  const selectedOnFocusRef = useRef(false)
  // Skip exactly one debounce cycle: set when we programmatically fill the input from a
  // chosen suggestion, so selecting an item does not immediately re-query it.
  const skipNextAutocompleteRef = useRef(false)
  useEffect(
    () => () => {
      abortRef.current?.abort()
      acAbortRef.current?.abort()
    },
    [],
  )

  // T-429: "in use" = focused OR still holding a query. Keeping the query in the condition is
  // what stops the card flashing back over the dropdown when focus moves to a suggestion, and
  // means an abandoned-but-filled field does not re-prompt "select a location to begin".
  const searchActive = focused || query.trim().length > 0
  useEffect(() => {
    onSearchActiveChange?.(searchActive)
  }, [searchActive, onSearchActiveChange])

  // ── T-134: debounced autocomplete ──────────────────────────────────────────────
  // Trailing debounce (AUTOCOMPLETE_DEBOUNCE_MS) + min-char gating + AbortController so
  // only the latest request resolves. Fast typing collapses to a single call after the pause;
  // typing below the threshold clears the dropdown and issues no request.
  useEffect(() => {
    if (skipNextAutocompleteRef.current) {
      skipNextAutocompleteRef.current = false
      return
    }

    const trimmed = query.trim()
    if (trimmed.length < AUTOCOMPLETE_MIN_CHARS) {
      // Below threshold: no request, and drop any stale suggestions.
      setSuggestions([])
      setActiveIndex(-1)
      return
    }

    const timer = setTimeout(() => {
      // Cancel any previous in-flight suggestion request; arm a fresh controller.
      acAbortRef.current?.abort()
      const controller = new AbortController()
      acAbortRef.current = controller
      const { signal } = controller

      void (async () => {
        try {
          const res = await authedFetch(
            `/api/geocode/autocomplete?q=${encodeURIComponent(trimmed)}`,
            { signal },
          )
          if (signal.aborted) return
          if (!res.ok) {
            setSuggestions([])
            return
          }
          const data = (await res.json()) as { suggestions?: AutocompleteSuggestion[] }
          if (signal.aborted) return
          setSuggestions(data.suggestions ?? [])
          setActiveIndex(-1)
        } catch (err) {
          // Superseded/unmounted requests surface as AbortError — expected, swallow.
          if ((err as Error)?.name === 'AbortError') return
          setSuggestions([])
        }
      })()
    }, AUTOCOMPLETE_DEBOUNCE_MS)

    // Trailing debounce: a new keystroke cancels the pending fetch before it fires.
    return () => clearTimeout(timer)
  }, [query])

  async function handleSearch(overrideQuery?: string) {
    const trimmed = (overrideQuery ?? query).trim()
    if (!map || !trimmed) return

    setShowRecent(false)
    setLoading(true)
    setError(null)

    // Cancel any previous in-flight search; arm a fresh controller for this one.
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const { signal } = controller

    try {
      // 1. Geocode the address — a SINGLE call. A plain empty result ([]) is a genuine
      //    "no such place"; we do NOT retry it (T-081 F9: the old retry-once-on-empty couldn't
      //    tell a soft-throttle from a real miss, so it fired a second identical Nominatim POST
      //    on every genuine no-result search — doubling load and worsening the very throttling
      //    it tried to dodge). Throttle-resilience lives server-side: the /api/geocode route's
      //    nominatimFetch already retries 429/5xx with backoff and surfaces a persistent
      //    throttle as a non-2xx (handled by the !geoRes.ok branch), so a client retry here
      //    would only re-hammer the upstream it protects.
      const geoRes = await authedFetch('/api/geocode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: trimmed }),
        signal,
      })
      if (signal.aborted) return // BEAD-0037: bail before touching the (maybe removed) map
      if (!geoRes.ok) {
        setError('Geocoding failed. Please try a different address.')
        return
      }
      const results = (await geoRes.json()) as GeocodeResult[]
      if (signal.aborted) return

      const first = results[0]
      if (!first) {
        setError('No results found. Try a more specific address.')
        return
      }

      const { lat, lng } = first
      // Plan 02.1-06: extract confidence; default to 'approximate' if absent
      const confidence: 'exact' | 'approximate' = first.confidence ?? 'approximate'

      // 2. Fly the map to the geocoded location.
      //    T-155 (BEAD-0062): zoom IN at least to the POI gate (POI_MIN_ZOOM) so the
      //    zoom-gated POI layer and other location data load immediately after a search,
      //    instead of leaving the user at a level where the POI toggle is disabled. But
      //    never zoom a user OUT — if they are already deeper than the gate, keep that
      //    zoom (max, never widen).
      const targetZoom = Math.max(map.getZoom(), POI_MIN_ZOOM)
      map.flyTo({ center: [lng, lat], zoom: targetZoom })

      // 3. Log the search event (fire-and-forget; failure is non-fatal — IN-02)
      authedFetch('/api/events/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: trimmed, lat, lng }),
      }).catch(() => {
        /* non-fatal: engagement logging; ignore */
      })

      // 4. Resolve the census tract CONTAINING the geocoded point (T-153 / BEAD-0060).
      //    Point-in-polygon via GET /api/tracts/at — the same ST_Contains basis /api/score/site
      //    uses — NOT the old /api/tracts/bbox → features[0]. That bbox path took an ARBITRARY
      //    neighbour intersecting a ~1km box (getTractsInBbox has no ORDER BY), so the RHS panel
      //    showed the correct score against the WRONG tract GEOID (Journal Square, NJ: it showed
      //    34017000902, ~390 m away, while the map showed the containing 34017001900).
      //    BEAD-0038: ALWAYS call onSearchResult — even when no tract contains the point (out of
      //    coverage) — with tract:null, so MapShell funnels through the shared
      //    score→404→"No coverage" path rather than dead-ending on a silent pan.
      const tractParams = new URLSearchParams({ lat: String(lat), lng: String(lng) })

      let tract: CensusTract | null = null
      const tractRes = await authedFetch(`/api/tracts/at?${tractParams}`, { signal })
      if (signal.aborted) return
      if (tractRes.ok) {
        const fc = (await tractRes.json()) as { features?: { properties: unknown }[] }
        if (signal.aborted) return
        const topFeature = fc.features?.[0]
        if (topFeature) {
          tract = featurePropsToTract(topFeature.properties as TractFeatureProps)
        }
      }
      onSearchResult(tract, { lat, lng }, confidence, trimmed)
      // Task 5: record the query in recent searches (a valid geocode result was found).
      addRecentSearch(trimmed)
    } catch (err) {
      // Aborted fetches surface as AbortError — expected on unmount, swallow silently.
      if ((err as Error)?.name === 'AbortError') return
      // IN-02: do not log internal error details (including stack traces) to the
      // browser console in production; surface a user-facing message instead.
      setError('An error occurred. Please try again.')
    } finally {
      // Skip the state update when aborted (component is unmounting).
      if (!signal.aborted) setLoading(false)
    }
  }

  // T-134: choosing a suggestion fills the input and drives the existing geocode/pin flow.
  // We suppress the next debounce cycle so the fully-selected text is not re-queried.
  function handleSuggestionSelect(text: string) {
    skipNextAutocompleteRef.current = true
    acAbortRef.current?.abort()
    setQuery(text)
    setSuggestions([])
    setActiveIndex(-1)
    setShowRecent(false)
    void handleSearch(text)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // T-134: when the suggestion dropdown is open, arrow keys move the highlight and Enter
    // selects the highlighted item. With no highlight (or no dropdown), Enter runs the
    // existing free-text search.
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex(Math.min(activeIndexRef.current + 1, suggestions.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex(Math.max(activeIndexRef.current - 1, -1))
        return
      }
      if (e.key === 'Escape') {
        setSuggestions([])
        setActiveIndex(-1)
        return
      }
      if (e.key === 'Enter' && activeIndexRef.current >= 0) {
        e.preventDefault()
        const chosen = suggestions[activeIndexRef.current]
        if (chosen) handleSuggestionSelect(chosen.text)
        return
      }
    }
    if (e.key === 'Enter') void handleSearch()
  }

  function handleRecentClick(item: string) {
    setQuery(item)
    setShowRecent(false)
    void handleSearch(item)
  }

  // Task 5: show the recent dropdown on focus when there are saved searches.
  // T-134: the autocomplete dropdown takes precedence over recents once we have suggestions
  // (they only appear at ≥ AUTOCOMPLETE_MIN_CHARS, so the two never both apply).
  const suggestionsVisible = suggestions.length > 0
  const recentVisible = !suggestionsVisible && showRecent && recentSearches.length > 0

  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <div className="flex gap-2 rounded-lg overflow-hidden bg-card shadow-md">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={(e) => {
              setFocused(true)
              setShowRecent(true)
              // Returning to a box that still holds the last address almost always means "search
              // somewhere else" (T-236). Selecting it makes typing replace it, instead of making
              // the user clear ~40 characters by hand first. Only when there IS something to
              // replace — selecting an empty field is a no-op that would still eat the next click.
              if (e.currentTarget.value !== '') {
                selectedOnFocusRef.current = true
                e.currentTarget.select()
              }
            }}
            onMouseUp={(e) => {
              if (!selectedOnFocusRef.current) return
              selectedOnFocusRef.current = false
              e.preventDefault()
            }}
            // Delay hide so a click on a dropdown item registers before blur closes it. The
            // focus flag is NOT delayed (T-429): with the field empty there is nothing to click
            // through to, and with text in it `searchActive` stays true regardless.
            onBlur={() => {
              setFocused(false)
              setTimeout(() => {
                setShowRecent(false)
                setSuggestions([])
                setActiveIndex(-1)
              }, 150)
            }}
            placeholder="Search address…"
            disabled={loading}
            aria-label="Address search"
            className="flex-1 px-3.5 py-2.5 border-none outline-none text-sm text-foreground bg-transparent placeholder:text-muted-foreground disabled:opacity-50"
          />
          <button
            onClick={() => void handleSearch()}
            disabled={loading || !query.trim()}
            aria-label="Search"
            className="flex items-center gap-1.5 px-4 py-2.5 bg-primary text-primary-foreground text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Spinner size={14} label="Searching" />
                Searching…
              </>
            ) : (
              'Search'
            )}
          </button>
        </div>

        {/* T-134: search-as-you-type suggestions dropdown. Keyboard-navigable (ArrowUp/Down
            + Enter handled on the input); mouseDown (not click) selects before the input's
            blur handler closes the list. Takes precedence over the recent-searches dropdown. */}
        {suggestionsVisible && (
          <ul
            role="listbox"
            aria-label="Address suggestions"
            className="absolute left-0 right-0 top-full mt-1 rounded-lg bg-card shadow-md overflow-hidden z-20"
          >
            {suggestions.map((s, i) => (
              <li key={s.placeId || s.text} role="option" aria-selected={i === activeIndex}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleSuggestionSelect(s.text)
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`w-full text-left px-3 py-2 text-sm text-foreground ${
                    i === activeIndex
                      ? 'bg-black/5 dark:bg-white/5'
                      : 'hover:bg-black/5 dark:hover:bg-white/5'
                  }`}
                >
                  {s.text}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Task 5: recent searches dropdown */}
        {recentVisible && (
          <div className="absolute left-0 right-0 top-full mt-1 rounded-lg bg-card shadow-md overflow-hidden z-20">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/60">
              <span className="text-[0.625rem] font-bold uppercase tracking-widest text-muted-foreground">
                Recent
              </span>
              <button
                // onMouseDown (not onClick) so it fires before the input's blur handler.
                onMouseDown={(e) => {
                  e.preventDefault()
                  clearRecentSearches()
                }}
                className="text-[0.625rem] text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            </div>
            <ul>
              {recentSearches.map((item) => (
                <li key={item}>
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleRecentClick(item)
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Plan 02-04: pin affordance hint — hidden once pin is placed or error is shown */}
      {!pinPlaced && !error && (
        <p className="text-xs text-muted-foreground px-0.5">or click the map to drop a pin</p>
      )}
      {error && (
        <p className="m-0 px-2 py-1.5 bg-destructive/10 text-destructive rounded-md text-xs">
          {error}
        </p>
      )}
    </div>
  )
}
